import React from "react";
import { Trans } from "@lingui/macro";
import { ColumnTitle } from "../ColumnTitle";
import { Loader } from "components/UI/Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { ISystemState } from "model/system/systemReducer";
import { IdOrdersState } from "model/dashboardOrders/dOrdersReducer";
import { addNotificationAC } from "model/system/systemActionCreators";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { NULL_VALUE, SelectBox } from "components/UI/SelectBox/SelectBox";
import { psidValidation } from "components/containers/dashboard/dataProducts";
import { IdProductsStateDataItem } from "model/dashboardProducts/dProductsReducer";
import { IdCategoriesStateDataItem } from "model/dashboardCategories/dCategoriesReducer";
import { psidValidationEnv } from "components/containers/dashboard/dataProducts/constants";
import {
  dOrdersAddProductToCartAC,
  dOrdersSetDetailProductAC,
  dOrdersSetProductFiltersAC,
  dOrdersProductsSetPaginationAC,
  dOrdersSetFilteredProductsSagaAC,
  dOrdersSetCountForProductInCartItemAC,
} from "model/dashboardOrders/dOrdersActionCreators";
import {
  PaginationDashboard,
  typePaginationDashboardObject,
} from "../../DashboardContentTable/PaginationDashboard/PaginationDashboard";

export type typeProducts = {
  loaderCategoryList: boolean;
  categoryList: IdCategoriesStateDataItem[];
  setOpenEnterMarkLabel: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Products: React.FC<typeProducts> = ({
  categoryList,
  loaderCategoryList,
  setOpenEnterMarkLabel,
}) => {
  const dispatch = useDispatch();
  const ref = React.useRef<HTMLDivElement>(null);

  const loader = useSelector(
    (state: { system: ISystemState }) => state.system.dashboardLoader,
  );

  const filters = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.productsFilter,
  );

  const [categoryIdValue, setCategoryIdValue] = React.useState<string | null>(
    filters && filters.category ? filters.category : null,
  );

  const productList = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.filteredProductList,
  );

  const selectedStore = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.selectedStore,
  );

  const currentCart = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.newOrder,
  );

  const pagination: typePaginationDashboardObject = {
    paginationObject: useSelector(
      (state: { dOrders: IdOrdersState }) => state.dOrders.productsPagination,
    ),
    onSelectItems: (itemsPerPage) => {
      dispatch(
        dOrdersProductsSetPaginationAC({
          pageNumber: 0,
          itemsPerPage: itemsPerPage,
        }),
      );
    },
    onGoToPage: (id) => {
      dispatch(dOrdersProductsSetPaginationAC({ pageNumber: id }));
    },
  };

  React.useEffect(() => {
    if (!!filters && pagination.paginationObject.itemsPerPage) {
      if (ref.current) {
        ref.current.scroll(0, 0);
      }

      dispatch(
        dOrdersSetFilteredProductsSagaAC(filters!, {
          page: pagination.paginationObject.pageNumber,
          size: pagination.paginationObject.itemsPerPage,
        }),
      );
    }
    // eslint-disable-next-line
  }, [
    filters,
    pagination.paginationObject.pageNumber,
    pagination.paginationObject.itemsPerPage,
  ]);

  React.useEffect(() => {
    if (categoryIdValue && categoryIdValue !== NULL_VALUE.id) {
      dispatch(dOrdersSetProductFiltersAC({ category: categoryIdValue }));
    }
    // eslint-disable-next-line
  }, [categoryIdValue]);

  const onProductClickHandler = (
    product: IdProductsStateDataItem,
    price: number | undefined,
  ) => {
    if (
      psidValidationEnv &&
      (product.psid === null || !psidValidation(product.psid))
    ) {
      const id = new Date();
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: "ERROR_INVALID_PSID",
        }),
      );
      return;
    }
    if (price) {
      if (product.marked) {
        setOpenEnterMarkLabel(true);
        dispatch(dOrdersSetDetailProductAC(product));
      } else {
        const existProduct = currentCart?.productList.find(
          (item) => item.product.id === product.id,
        );
        if (existProduct) {
          dispatch(
            dOrdersSetCountForProductInCartItemAC(
              product.id,
              existProduct.amount + 1,
            ),
          );
        } else {
          dispatch(dOrdersAddProductToCartAC(product, price));
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-2 sm:gap-4 h-full">
      <ColumnTitle title={<Trans>Products</Trans>} />
      <div className="sm:hidden">
        {loaderCategoryList && <Loader />}
        <SelectBox
          valuesList={categoryList}
          label={<Trans>Category</Trans>}
          valueId={categoryIdValue}
          setIdValue={setCategoryIdValue}
        />
      </div>
      <div className="flex-grow relative h-60 overflow-y-auto sm:h-auto">
        <div ref={ref} className="absolute inset-0 overflow-y-auto">
          <div>
            {productList && productList?.length !== 0 ? (
              productList?.map((item, index) => {
                const store = item.stores.find(
                  (item) => item.store.id === selectedStore!.id,
                );
                const price = store ? (
                  numberCurrencyFormat(store.price)
                ) : (
                  <div className={"flex flex-wrap text-xxs text text-center"}>
                    <Trans>No price for this store</Trans>
                  </div>
                );
                return (
                  <button
                    key={item.id}
                    type={"button"}
                    onClick={() => {
                      onProductClickHandler(item, store?.price);
                    }}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50 " : ""
                    } p-0 m-0 w-full rounded-md hover:bg-indigo-100 focus:outline-none `}
                  >
                    <div className="gap-2 w-full flex justify-between px-2 py-1 sm:px-4 sm:py-2">
                      <div title={item.name} className="text-left truncate">
                        {item.name}
                      </div>
                      <div className="text-right self-center whitespace-nowrap">
                        {price}
                      </div>
                    </div>
                  </button>
                );
              })
            ) : (
              <div>
                <p className={"text-sm mb-2"}>
                  <Trans>There are no products.</Trans>
                </p>
                <p className="text-xs text-gray-500">
                  <Trans>
                    {" "}
                    You can search for products by name, in the search bar
                    above, or select a product category, search results or
                    products from the selected category will be listed here.
                  </Trans>
                </p>
              </div>
            )}
          </div>
        </div>
        {loader && <Loader />}
      </div>
      {pagination.paginationObject && (
        <PaginationDashboard
          size="sm"
          buttonType="button"
          pagination={pagination}
        />
      )}
    </div>
  );
};
